import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getStorageItem, deleteStorageItem } from "../../utils/sessionStorage";
import { useUserStore } from "../../store/userStore";
import toast from "react-hot-toast";
import { AppointmentHistory } from "../../components/interfaces";
import { useAppointmentStore } from "../../store/appointmentHistoryStore";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import Loading from "../../components/loader";
import { AppointmentCard } from "./appointmenCardLayout";
import { API } from "../../utils/axios";
import { motion, AnimatePresence } from "framer-motion";

export default function UserBookings() {
  const navigate = useNavigate();
  const user = useUserStore((state) => state.user);
  const {
    appointments,
    appointmentsFetched,
    setAppointmentsFetched,
    updateAppointments,
  } = useAppointmentStore();
  const [isAppointmentLoading, setIsAppointmentLoading] = useState(false);
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [pastAppointments, setPastAppointments] = useState<
    AppointmentHistory[]
  >([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState<
    AppointmentHistory[]
  >([]);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [filter, setFilter] = useState<"all" | "upcoming" | "past">("all");

  const cancelAppointment = async (appointmentId: number, slotId: number, cancelReason: string) => {
    try {
      const res = await API.patch("/appointment/cancel", {
        appointmentId,
        slotId,
        reason: cancelReason
      });

      res?.data?.success ? toast.success(res?.data?.message) : toast.error(res?.data?.message)

      if (res.data.success) {
        fetchUserAppointments();
      }
    } catch (error) {
      console.error("Cancel failed:", error);
    }
  };

  const fetchUserAppointments = async () => {
    if (!user?.id) {
      toast.error("Please Login First");
      setTimeout(() => {
        navigate("/create-account");
      }, 2000);
      return;
    }
    setIsAppointmentLoading(true);

    try {
      await API.get("/appointment")
        .then((res: any) => {
          setAppointmentsFetched(true);
          updateAppointmentsByTime(res.data?.data);
          updateAppointments(res.data?.data);
        })
        .finally(() => {
          setIsAppointmentLoading(false);
        });
    } catch (err) {
      console.log("Error in fetching user appointments", err);
    }
  };

  function updateAppointmentsByTime(appointments: AppointmentHistory[]) {
    if (!appointments.length) {
      setUpcomingAppointments([]);
      setPastAppointments([]);
      return;
    }

    const now = new Date();
    let splitIndex = 0;

    for (const appointment of appointments) {
      if (new Date(appointment.bookingStartTime) < now) {
        break;
      }
      splitIndex++;
    }

    setUpcomingAppointments(appointments.slice(0, splitIndex));
    setPastAppointments(appointments.slice(splitIndex));
  }

  useEffect(() => {
    if (appointments.length === 0 && !appointmentsFetched) {
      fetchUserAppointments();
    } else {
      updateAppointmentsByTime(appointments);
    }
  }, []);

  const filteredData = useMemo(() => {
    if (filter === "upcoming") return upcomingAppointments;
    if (filter === "past") return pastAppointments;
    return [...upcomingAppointments, ...pastAppointments].sort(
      (a, b) =>
        new Date(b.appointmentDate).getTime() -
        new Date(a.appointmentDate).getTime(),
    );
  }, [filter, upcomingAppointments, pastAppointments]);

  return (
    <main className="min-h-screen bg-[#F8FAFC] p-4 md:p-12">
      <div className="max-w-5xl mx-auto">
        {/* --- HEADER SECTION --- */}
        <header className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-6">
          <div className="space-y-1">
            <h1 className="text-4xl font-semibold text-gray-900 tracking-tight">
              My Bookings
            </h1>
            <p className="text-gray-500 font-medium">
              Manage your appointments and history
            </p>
          </div>

          {/* Modern Filter Pill */}
          <div className="flex bg-gray-200/50 backdrop-blur-md p-1.5 rounded-2xl w-fit border border-gray-200">
            {["all", "upcoming", "past"].map((type) => (
              <button
                key={type}
                onClick={() => setFilter(type as any)}
                className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
                  filter === type
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
        </header>

        {/* --- CONTENT SECTION --- */}
        <section>
          {isAppointmentLoading ? (
            <Loading size={100} message="Loading your Bookings" />
          ) : filteredData.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-20 bg-white rounded-[2.5rem] border-2 border-dashed border-gray-200"
            >
              <h2 className="text-xl font-bold text-gray-800">
                No appointments found
              </h2>
              <p className="text-gray-500 mb-8">
                You don't have any {filter !== "all" ? filter : ""} bookings
                yet.
              </p>
              <button
                onClick={() => navigate("/serviceList")}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl transition-all font-semibold shadow-lg shadow-blue-100 active:scale-95"
              >
                Book Now
              </button>
            </motion.div>
          ) : (
            <div className="grid gap-5">
              <AnimatePresence mode="popLayout">
                {filteredData.map((appointment) => (
                  <AppointmentCard
                    key={appointment.appointment_id}
                    appointment={appointment}
                    isUpcoming={
                      new Date(appointment?.bookingStartTime) > new Date()
                    }
                    userTimeZone={userTimeZone}
                    isCancelling={cancellingId === appointment.appointment_id}
                    setCancellingId={setCancellingId}
                    onCancel={cancelAppointment}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
